import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { 
  TreeDeciduous, Plus, Users, Calendar, LogOut, 
  GitBranch, ChevronRight, Sparkles, HelpCircle, Shield, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [globalAdminOpen, setGlobalAdminOpen] = useState(false);
  const [globalAdmins, setGlobalAdmins] = useState([]);
  const [newGlobalAdminEmail, setNewGlobalAdminEmail] = useState("");
  const [newBranch, setNewBranch] = useState({
    name: "",
    description: "",
    privacy: "admin_only"
  });

  useEffect(() => {
    // Check if there's a pending redirect (e.g., from an invite link)
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath, { replace: true });
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [branchesRes, statsRes] = await Promise.all([
        fetch(`${API}/branches`, { credentials: "include" }),
        fetch(`${API}/dashboard/stats`, { credentials: "include" })
      ]);

      if (branchesRes.ok) {
        const branchesData = await branchesRes.json();
        setBranches(branchesData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBranch = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/branches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newBranch)
      });

      if (!response.ok) throw new Error("Failed to create branch");

      const branch = await response.json();
      setBranches([...branches, { ...branch, is_admin: true, member_count: 0 }]);
      setCreateDialogOpen(false);
      setNewBranch({ name: "", description: "", privacy: "admin_only" });
      toast.success("Branch created successfully!");
      
      // Update stats
      if (stats) {
        setStats({ ...stats, total_branches: stats.total_branches + 1 });
      }
    } catch (error) {
      console.error("Error creating branch:", error);
      toast.error("Failed to create branch");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const fetchGlobalAdmins = async () => {
    try {
      const response = await fetch(`${API}/admin/global-admins`, { credentials: "include" });
      if (response.ok) {
        const admins = await response.json();
        setGlobalAdmins(admins);
      }
    } catch (error) {
      console.error("Error fetching global admins:", error);
    }
  };

  const handleAddGlobalAdmin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/admin/global-admins/${encodeURIComponent(newGlobalAdminEmail)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_global_admin: true })
      });
      
      if (response.ok) {
        toast.success(`${newGlobalAdminEmail} is now a Global Admin!`);
        setNewGlobalAdminEmail("");
        fetchGlobalAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add global admin");
      }
    } catch (error) {
      toast.error("Failed to add global admin");
    }
  };

  const handleRemoveGlobalAdmin = async (email) => {
    try {
      const response = await fetch(`${API}/admin/global-admins/${encodeURIComponent(email)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_global_admin: false })
      });
      
      if (response.ok) {
        toast.success(`${email} removed from Global Admins`);
        fetchGlobalAdmins();
      } else {
        toast.error("Failed to remove global admin");
      }
    } catch (error) {
      toast.error("Failed to remove global admin");
    }
  };

  // Check if user is an admin of any branch (only admins can create new branches)
  const isAdmin = branches.some(branch => branch.is_admin);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TreeDeciduous className="w-8 h-8 text-primary" />
            <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/help")}
              data-testid="help-btn"
              title="Help"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            {isAdmin && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => { setGlobalAdminOpen(true); fetchGlobalAdmins(); }}
                data-testid="global-admin-btn"
                title="Manage Global Admins"
              >
                <Shield className="w-5 h-5" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-lg text-muted-foreground">
            Manage your family tree branches and explore your heritage.
          </p>
        </div>

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <GitBranch className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{stats?.total_branches || 0}</p>
                  <p className="text-sm text-muted-foreground">Branches</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{stats?.total_members || 0}</p>
                  <p className="text-sm text-muted-foreground">Family Members</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-info" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{stats?.total_events || 0}</p>
                  <p className="text-sm text-muted-foreground">Timeline Events</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Only show Create Branch for admins */}
          {isAdmin && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Card 
                  className="bg-primary/5 rounded-2xl border-2 border-dashed border-primary/30 shadow-sm cursor-pointer hover:bg-primary/10 hover:border-primary/50 transition-all duration-300"
                  data-testid="create-branch-card"
                >
                  <CardContent className="p-6 flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                        <Plus className="w-6 h-6 text-primary" />
                      </div>
                      <p className="font-medium text-primary">Create New Branch</p>
                    </div>
                  </CardContent>
                </Card>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Create New Branch</DialogTitle>
                <DialogDescription>
                  Start a new family branch to organize your relatives.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateBranch} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="branch-name">Branch Name</Label>
                  <Input
                    id="branch-name"
                    data-testid="branch-name-input"
                    placeholder="e.g., Smith Family"
                    value={newBranch.name}
                    onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch-description">Description (Optional)</Label>
                  <Textarea
                    id="branch-description"
                    data-testid="branch-description-input"
                    placeholder="Add a brief description..."
                    value={newBranch.description}
                    onChange={(e) => setNewBranch({ ...newBranch, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch-privacy">Privacy Setting</Label>
                  <Select
                    value={newBranch.privacy}
                    onValueChange={(value) => setNewBranch({ ...newBranch, privacy: value })}
                  >
                    <SelectTrigger id="branch-privacy" data-testid="branch-privacy-select">
                      <SelectValue placeholder="Select privacy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_only">Admin Only</SelectItem>
                      <SelectItem value="all_members">All Members</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="create-branch-submit">
                    Create Branch
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Branches Grid */}
        <div className="mb-10">
          <h2 className="font-display text-2xl font-semibold text-foreground mb-6">Your Branches</h2>
          
          {branches.length === 0 ? (
            <Card className="bg-card rounded-2xl border border-border/60 p-12 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl font-medium text-foreground mb-2">
                No branches yet
              </h3>
              <p className="text-muted-foreground mb-6">
                {isAdmin 
                  ? "Create your first family branch to get started"
                  : "You haven't been invited to any branches yet. Ask a family admin to send you an invite link."
                }
              </p>
              {isAdmin && (
                <Button 
                  onClick={() => setCreateDialogOpen(true)}
                  data-testid="empty-create-branch-btn"
                  className="bg-primary text-primary-foreground rounded-full px-6"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create First Branch
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {branches.map((branch) => (
                <Card 
                  key={branch.branch_id}
                  className="bg-card rounded-2xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer hover:border-primary/30 hover:ring-1 hover:ring-primary/30"
                  onClick={() => navigate(`/branch/${branch.branch_id}`)}
                  data-testid={`branch-card-${branch.branch_id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <GitBranch className="w-5 h-5 text-primary" />
                      </div>
                      {branch.is_admin && (
                        <span className="text-xs font-medium bg-accent/10 text-accent px-2 py-1 rounded-full">
                          Admin
                        </span>
                      )}
                    </div>
                    <CardTitle className="font-display text-xl mt-3">{branch.name}</CardTitle>
                    <CardDescription className="line-clamp-2">
                      {branch.description || "No description"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        <Users className="w-4 h-4 inline mr-1" />
                        {branch.member_count || 0} members
                      </span>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        {(stats?.recent_members?.length > 0 || stats?.recent_events?.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {stats?.recent_members?.length > 0 && (
              <Card className="bg-card rounded-2xl border border-border/60 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-xl">Recent Members</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.recent_members.map((member) => (
                      <div 
                        key={member.member_id}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/member/${member.member_id}`)}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.photo_url} />
                          <AvatarFallback className="bg-secondary text-secondary-foreground">
                            {member.first_name?.charAt(0)}{member.last_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {member.location || "No location"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {stats?.recent_events?.length > 0 && (
              <Card className="bg-card rounded-2xl border border-border/60 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-xl">Recent Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.recent_events.map((event) => (
                      <div 
                        key={event.event_id}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{event.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {event.event_date} · {event.event_type}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Global Admin Dialog */}
      <Dialog open={globalAdminOpen} onOpenChange={setGlobalAdminOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" /> Global Admins
            </DialogTitle>
            <DialogDescription>
              Global Admins can manage ALL branches in the family archive
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddGlobalAdmin} className="flex gap-2 mt-4">
            <Input
              type="email"
              placeholder="Enter email address"
              value={newGlobalAdminEmail}
              onChange={(e) => setNewGlobalAdminEmail(e.target.value)}
              required
              data-testid="global-admin-email"
            />
            <Button type="submit" data-testid="add-global-admin-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </form>
          
          <div className="space-y-3 mt-4 max-h-64 overflow-y-auto">
            {globalAdmins.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No global admins yet</p>
            ) : (
              globalAdmins.map((admin) => (
                <div key={admin.user_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={admin.picture} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {(admin.name || admin.email || "?").charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-foreground">{admin.name || "Unknown"}</p>
                      <p className="text-sm text-muted-foreground">{admin.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveGlobalAdmin(admin.email)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`remove-admin-${admin.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setGlobalAdminOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
